import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';

import { AvEmptyStateComponent } from '../../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvDonutChartComponent } from '../../../../../Shared/Components/av-donut-chart/av-donut-chart.component';
import { TranslationService } from '../../../../../Shared/Services/translation.service';
import { COMMON_IMPORTS } from '../../../../../Shared/common-imports';
import {
  MONITORING_KINDS,
  MONITORING_STATUS_ANNOUNCEMENT_KEYS,
  MONITORING_STATUS_BUCKETS,
} from '../../../Consts/monitoring-chart.const';
import { MonitoringByStatus } from '../../../Interfaces/monitoring-report.interface';
import { MonitoringStatusLegendRow } from '../../../Interfaces/monitoring-status-legend-row.interface';
import { sumStatusBuckets, toStatusSegments } from '../../../Shared/monitoring-chart-data.util';
import { MonitoringStatusBucket } from '../../../Types/monitoring-status-bucket.type';

@Component({
  selector: 'app-monitoring-status-breakdown',
  standalone: true,
  imports: [COMMON_IMPORTS, AvDonutChartComponent, AvEmptyStateComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './monitoring-status-breakdown.component.html',
  styleUrl: './monitoring-status-breakdown.component.scss',
  host: { class: 'av-metric-card' },
})
export class MonitoringStatusBreakdownComponent {
  readonly byStatus = input.required<MonitoringByStatus>();

  private readonly _translation = inject(TranslationService);
  private readonly _hidden = signal<ReadonlySet<MonitoringStatusBucket>>(new Set());

  protected readonly announcement = signal('');

  protected readonly legend = computed<MonitoringStatusLegendRow[]>(() =>
    MONITORING_STATUS_BUCKETS.map((definition) => ({
      ...definition,
      visible: !this._hidden().has(definition.bucket),
    })),
  );

  protected readonly kinds = computed(() => {
    const hidden = this._hidden();
    return MONITORING_KINDS.map(({ kind, labelKey }) => {
      const buckets = this.byStatus()[kind];
      return {
        labelKey,
        segments: toStatusSegments(buckets, hidden),
        total: sumStatusBuckets(buckets),
        visibleTotal: sumStatusBuckets(buckets, hidden),
      };
    });
  });

  protected readonly isEmpty = computed(() => this.kinds().every((kind) => kind.total === 0));

  protected toggleStatus(row: MonitoringStatusLegendRow): void {
    const hidden = new Set(this._hidden());
    if (row.visible) {
      hidden.add(row.bucket);
    } else {
      hidden.delete(row.bucket);
    }
    this._hidden.set(hidden);
    this.announcement.set(this._announcementFor(row));
  }

  private _announcementFor(row: MonitoringStatusLegendRow): string {
    const status = this._translation.instant(row.labelKey);
    if (!row.visible) {
      return this._translation.instant(MONITORING_STATUS_ANNOUNCEMENT_KEYS.shown, { status });
    }
    const nothingShown = this.kinds().every((kind) => kind.visibleTotal === 0);
    const key = nothingShown
      ? MONITORING_STATUS_ANNOUNCEMENT_KEYS.allHidden
      : MONITORING_STATUS_ANNOUNCEMENT_KEYS.hidden;
    return this._translation.instant(key, { status });
  }
}
